import { Controller } from '@hotwired/stimulus'
import {
  buildSignupsChart,
  buildGrowthChart,
  buildEngagementChart,
  buildSessionsChart,
  buildAccountChart,
  buildInvitationsChart
} from '~/javascript/charts'

export default class extends Controller {
  static targets = [
    'signupsCanvas',
    'growthCanvas',
    'engagementCanvas',
    'sessionsCanvas',
    'accountCanvas',
    'invitationsCanvas',
    'loading',
    'error'
  ]

  static values = {
    url: String,
    startDate: String,
    endDate: String
  }

  charts = {}

  connect() {
    this.loadCharts()
  }

  disconnect() {
    this.destroyCharts()
  }

  destroyCharts() {
    Object.values(this.charts).forEach((chart) => chart.destroy())
    this.charts = {}
  }

  async loadCharts() {
    this.showLoading()
    this.hideError()

    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set('start_date', this.startDateValue)
      url.searchParams.set('end_date', this.endDateValue)

      const response = await fetch(url, {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      this.renderCharts(data.charts)
    } catch (error) {
      console.error('Failed to load chart data:', error)
      this.showError()
    } finally {
      this.hideLoading()
    }
  }

  renderCharts(chartsData) {
    this.destroyCharts()

    if (this.hasSignupsCanvasTarget && chartsData.signups) {
      this.charts.signups = buildSignupsChart(this.signupsCanvasTarget, chartsData.signups)
    }

    if (this.hasGrowthCanvasTarget && chartsData.cumulative_growth) {
      this.charts.growth = buildGrowthChart(this.growthCanvasTarget, chartsData.cumulative_growth)
    }

    if (this.hasEngagementCanvasTarget && chartsData.engagement_distribution) {
      this.charts.engagement = buildEngagementChart(
        this.engagementCanvasTarget,
        chartsData.engagement_distribution
      )
    }

    if (this.hasSessionsCanvasTarget && chartsData.active_sessions) {
      this.charts.sessions = buildSessionsChart(
        this.sessionsCanvasTarget,
        chartsData.active_sessions
      )
    }

    if (this.hasAccountCanvasTarget && chartsData.account_growth) {
      this.charts.account = buildAccountChart(this.accountCanvasTarget, chartsData.account_growth)
    }

    if (this.hasInvitationsCanvasTarget && chartsData.invitations) {
      this.charts.invitations = buildInvitationsChart(
        this.invitationsCanvasTarget,
        chartsData.invitations
      )
    }
  }

  updateDateRange(event) {
    const { startDate, endDate } = event.detail
    this.startDateValue = startDate
    this.endDateValue = endDate
    this.loadCharts()
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('hidden')
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('hidden')
    }
  }

  showError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.remove('hidden')
    }
  }

  hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('hidden')
    }
  }
}
